// ============================================================
// GroupMappingsTab.js
// Group Mappings tab inside <ClientDetailPage>. List / create /
// soft-delete view for the per-Client GroupMappings table.
//
// Federation runtime resolves a federated user's groups via Graph
// and maps each (Client, group, role) tuple here back to a CareHub
// role. See withAuth.js Step 7 federation flow + Workstream 3.
//
// Role dropdown excludes 'auditor' — auditor is invitation-only and
// NOT federation-mappable (W3 design decision lock). The backend
// CHECK constraint enforces; the UI helper text explains.
//
// Group picker (W4): on the create form, an inline searchable
// combobox queries listAvailableGroups for this Client and renders
// real display names from the federated client tenant. If the
// available-groups endpoint returns federation_not_configured /
// federation_not_active / federation_consent_lost /
// federation_graph_unavailable, the form auto-reveals the GUID
// fallback with a contextual error banner explaining the cause —
// the operator can still complete the mapping by pasting the GUID
// from the client tenant's Entra admin centre.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-group-mappings-*.js
//   backend/func-carehub-api/src/functions/admin-clients-available-groups.js
// ============================================================

import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Plus, Trash2, Search, RefreshCw, AlertCircle } from 'lucide-react';
import DataTable from '../components/DataTable';
import Field from '../components/Field';
import ConfirmDialog from '../components/ConfirmDialog';
import { RoleBadge } from '../components/StatusBadge';
import { toast } from '../components/Toaster';
import {
  listGroupMappings,
  createGroupMapping,
  deleteGroupMapping,
  listAvailableGroups,
} from '../api/federation';
import CarePeopleRoleMappingsSection from './CarePeopleRoleMappingsSection';
import CareChargeRoleMappingsSection from './CareChargeRoleMappingsSection';
import CarePlanRoleMappingsSection from './CarePlanRoleMappingsSection';
import CareRotaRoleMappingsSection from './CareRotaRoleMappingsSection';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import './GroupMappingsTab.css';

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const ALLOWED_ROLES = ['manager', 'admin', 'carer'];

const ROLE_OPTIONS = [
  { value: 'manager', label: 'Manager' },
  { value: 'admin',   label: 'Admin' },
  { value: 'carer',   label: 'Carer' },
];

const EMPTY_FORM = { groupObjectId: '', groupDisplayName: '', role: '' };

// Pagination is hidden when totalCount === 0; for a non-empty list
// we still render the footer. Setting pageSize large (≥ realistic
// max) means totalPages=1 and prev/next are disabled. Acceptable.
const PAGE_SIZE = 200;

// Auto-reveal copy for the GUID fallback. Keyed on the four error
// codes from listAvailableGroups; any other code (500, 401, network)
// falls back to GUID mode with the generic copy keyed under 'unknown'.
const PICKER_FALLBACK_COPY = {
  federation_not_configured: {
    title: 'Federation not configured for this Tenant',
    body: 'Federation isn’t set up for this Client’s Tenant. Enter the group GUID manually below.',
    canRetry: false,
  },
  federation_not_active: {
    title: 'Federation not yet active',
    body: 'Federation consent hasn’t been granted yet (or has been revoked). Enter the group GUID manually below, or grant consent at the Tenant first.',
    canRetry: false,
  },
  federation_consent_lost: {
    title: 'Client tenant consent appears to have been revoked',
    body: 'The client tenant’s consent appears to have been revoked. Federation has been flagged for re-consent. Enter the group GUID manually below if you have it.',
    canRetry: true,
  },
  federation_graph_unavailable: {
    title: 'Couldn’t reach the client tenant',
    body: 'Couldn’t reach the client tenant. Enter the group GUID manually below if you have it, or retry the picker.',
    canRetry: true,
  },
  unknown: {
    title: 'Could not load groups',
    body: 'Something went wrong loading the group list. Enter the group GUID manually below if you have it, or retry the picker.',
    canRetry: true,
  },
};

// Four error codes auto-reveal the GUID fallback. Anything else
// (network, 500, etc.) falls into the 'unknown' bucket.
const AUTO_REVEAL_CODES = new Set([
  'federation_not_configured',
  'federation_not_active',
  'federation_consent_lost',
  'federation_graph_unavailable',
]);

function validateGroupObjectId(value) {
  if (!value) return 'Required';
  if (!GUID_REGEX.test(value)) return 'Must be a valid GUID';
  return null;
}

function validateRole(value) {
  if (!value) return 'Required';
  if (!ALLOWED_ROLES.includes(value)) return 'Must be manager, admin, or carer';
  return null;
}

function truncateGuid(s) {
  if (typeof s !== 'string' || s.length <= 13) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
}

export default function GroupMappingsTab({ clientId }) {
  const [mappings, setMappings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [mode, setMode] = useState('view'); // 'view' | 'creating'
  const [inFlight, setInFlight] = useState(null); // 'creating' | 'deleting' | null

  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // Picker state. Initialised when the create form opens; persists
  // for the lifetime of the form (cancel resets).
  const [pickerInput, setPickerInput]           = useState('');
  const [pickerSelectedId, setPickerSelectedId] = useState('');
  const [pickerOpen, setPickerOpen]             = useState(false);
  const [pickerState, setPickerState]           = useState('idle'); // idle|loading|loaded|empty|error
  const [availableGroups, setAvailableGroups]   = useState([]);
  const [hasMoreGroups, setHasMoreGroups]       = useState(false);
  const [pickerErrorCode, setPickerErrorCode]   = useState(null);
  const [useGuidFallback, setUseGuidFallback]   = useState(false);

  // Delete confirmation dialog — holds the row being deleted so the
  // confirm message can render with the role label, plus an inline
  // error string for failure paths (rendered inside the dialog body
  // rather than toasted, so the user can read the explanation
  // without the dialog auto-closing).
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteError, setDeleteError]         = useState(null);

  const pickerWrapperRef = useRef(null);

  // ── Initial fetch ──────────────────────────────────────────
  const fetchMappings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listGroupMappings(clientId);
      setMappings(res.groupMappings || []);
    } catch (err) {
      setLoadError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchMappings(); }, [fetchMappings]);

  // ── Picker fetch ───────────────────────────────────────────
  const fetchAvailableGroups = useCallback(async () => {
    setPickerState('loading');
    setPickerErrorCode(null);
    try {
      const res = await listAvailableGroups(clientId);
      const groups = Array.isArray(res && res.groups) ? res.groups : [];
      setAvailableGroups(groups);
      setHasMoreGroups(Boolean(res && res.hasMore));
      setPickerState(groups.length === 0 ? 'empty' : 'loaded');
      setUseGuidFallback(false);
    } catch (err) {
      const code = (err && err.error) || 'unknown';
      setPickerErrorCode(AUTO_REVEAL_CODES.has(code) ? code : 'unknown');
      setPickerState('error');
      // Auto-reveal GUID input on any error.
      setUseGuidFallback(true);
    }
  }, [clientId]);

  // ── Form lifecycle ─────────────────────────────────────────
  function openCreate() {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setPickerInput('');
    setPickerSelectedId('');
    setPickerOpen(false);
    setAvailableGroups([]);
    setHasMoreGroups(false);
    setPickerErrorCode(null);
    setUseGuidFallback(false);
    setMode('creating');
    fetchAvailableGroups();
  }

  function cancelCreate() {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setMode('view');
  }

  function updateFormField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  // ── Picker interactions ────────────────────────────────────

  // Close the dropdown on outside-click. Mouse-down beats focus so a
  // click on an option still fires before the dropdown is torn down.
  useEffect(() => {
    if (!pickerOpen) return undefined;
    function handleDocMouseDown(e) {
      if (pickerWrapperRef.current && !pickerWrapperRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [pickerOpen]);

  function selectPickerGroup(group) {
    setPickerSelectedId(group.id);
    setPickerInput(group.displayName || group.id);
    setPickerOpen(false);
    setForm((prev) => ({
      ...prev,
      groupObjectId:    group.id,
      groupDisplayName: group.displayName || '',
    }));
    // Clear any stale GUID validation message.
    setFormErrors((prev) => {
      if (!prev.groupObjectId) return prev;
      const next = { ...prev };
      delete next.groupObjectId;
      return next;
    });
  }

  function clearPickerSelection() {
    setPickerSelectedId('');
    setPickerInput('');
    setForm((prev) => ({ ...prev, groupObjectId: '', groupDisplayName: '' }));
  }

  const filteredGroups = useMemo(() => {
    const q = pickerInput.trim().toLowerCase();
    if (!q || availableGroups.length === 0) return availableGroups;
    // Don't filter once a group is selected and the input matches its
    // display name — the user has chosen, dropdown should show full list
    // if reopened.
    if (pickerSelectedId) return availableGroups;
    return availableGroups.filter((g) => {
      const name = (g.displayName || '').toLowerCase();
      const id   = g.id.toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [pickerInput, availableGroups, pickerSelectedId]);

  // ── Map API errors → form-level errors ─────────────────────
  function applyCreateApiError(err) {
    const status = err && err.status;
    const code   = err && err.error;
    const detail = err && err.detail;

    if (status === 400 && code === 'invalid_group_object_id') {
      setFormErrors({ groupObjectId: detail || 'Invalid group object ID' });
      return;
    }
    if (status === 400 && code === 'invalid_role') {
      const allowed = (err && err.allowedRoles)
        || (err && err.body && err.body.allowedRoles)
        || ALLOWED_ROLES;
      setFormErrors({ role: `Invalid role. Allowed: ${allowed.join(', ')}.` });
      return;
    }
    if (status === 409 && code === 'group_mapping_already_exists') {
      setFormErrors({
        _top: 'A group mapping with this GUID and role already exists for this Client.',
        _topSub: 'You can delete the existing mapping first if you need to recreate it, or use a different role.',
      });
      return;
    }
    setFormErrors({ _top: translateError(err) });
  }

  // ── Create handler ─────────────────────────────────────────
  async function handleCreate(e) {
    if (e) e.preventDefault();
    const errs = {};
    const guidErr = validateGroupObjectId(form.groupObjectId);
    if (guidErr) errs.groupObjectId = guidErr;
    const roleErr = validateRole(form.role);
    if (roleErr) errs.role = roleErr;
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }

    setInFlight('creating');
    try {
      const body = {
        groupObjectId: form.groupObjectId.toLowerCase(),
        role: form.role,
      };
      if (form.groupDisplayName) body.groupDisplayName = form.groupDisplayName;
      const created = await createGroupMapping(clientId, body);
      setMappings((prev) => [...(prev || []), created]);
      setForm(EMPTY_FORM);
      setFormErrors({});
      setMode('view');
      toast.success('Group mapping created.');
    } catch (err) {
      applyCreateApiError(err);
    } finally {
      setInFlight(null);
    }
  }

  // ── Delete handler ─────────────────────────────────────────
  // Pattern mirrors FederationTab.handleConfirmDeleteDomain (Gotcha
  // #N — 204 delete-bug surfaced 2026-05-19 trained us against the
  // toast-on-error / no-rollback pattern in the original code).
  //
  //   1. Snapshot the current list BEFORE the API call.
  //   2. Optimistically remove the row from local state.
  //   3. On success: close dialog, toast success.
  //   4. On error: rollback to snapshot, set INLINE error in
  //      dialog (do NOT toast — the user needs to read the
  //      explanation without the dialog auto-closing).
  //
  // Specific code branching for known states; generic fallback
  // routes through translateError.
  function openDelete(mapping) {
    setDeleteError(null);
    setDeleteCandidate(mapping);
  }

  function closeDelete() {
    if (inFlight === 'deleting') return;
    setDeleteCandidate(null);
    setDeleteError(null);
  }

  async function handleConfirmDelete() {
    if (!deleteCandidate) return;
    setDeleteError(null);

    const targetId = deleteCandidate.groupMappingId;
    const snapshot = mappings;

    // Optimistic remove.
    setMappings((prev) => (prev || []).filter((m) => m.groupMappingId !== targetId));
    setInFlight('deleting');
    try {
      await deleteGroupMapping(clientId, targetId);
      setDeleteCandidate(null);
      toast.success('Group mapping removed.');
    } catch (err) {
      // Rollback the optimistic remove.
      setMappings(snapshot);

      const status = err && err.status;
      const code   = err && err.error;
      if (status === 404 && code === 'group_mapping_not_found') {
        setDeleteError(
          'This mapping may have already been removed by another operator or '
          + 'in an earlier action. Refresh the list to verify, then close this dialog.'
        );
        return;
      }
      // Inline-scoped 500 copy that admits state uncertainty. NOT
      // promoted to the global errorMessages dictionary — `HTTP 500`
      // surfaces in ~20 places across the admin portal including
      // read paths where the "couldn't confirm the operation" copy
      // would be misleading (no mutation was attempted).
      if (status >= 500) {
        setDeleteError(
          "Couldn't confirm whether the delete succeeded. Refresh to check current "
          + "state — if the mapping is still listed, retry; if it's gone, the "
          + 'operation succeeded despite the error.'
        );
        return;
      }
      setDeleteError(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  // ── Render ─────────────────────────────────────────────────

  if (mode === 'creating') {
    return renderCreateForm({
      form,
      formErrors,
      inFlight,
      onChange: updateFormField,
      onSubmit: handleCreate,
      onCancel: cancelCreate,
      picker: {
        wrapperRef:      pickerWrapperRef,
        input:           pickerInput,
        setInput:        setPickerInput,
        selectedId:      pickerSelectedId,
        open:            pickerOpen,
        setOpen:         setPickerOpen,
        state:           pickerState,
        groups:          filteredGroups,
        totalGroups:     availableGroups.length,
        hasMore:         hasMoreGroups,
        errorCode:       pickerErrorCode,
        useGuidFallback,
        setUseGuidFallback,
        onSelect:        selectPickerGroup,
        onClear:         clearPickerSelection,
        onRetry:         fetchAvailableGroups,
      },
    });
  }

  const columns = [
    {
      key: 'displayName',
      label: 'Group display name',
      render: (row) => row.groupDisplayName || <span className="group-mappings-tab__muted">—</span>,
    },
    {
      key: 'objectId',
      label: 'Group object ID',
      render: (row) => (
        <span className="group-mappings-tab__guid" title={row.groupObjectId}>
          {truncateGuid(row.groupObjectId)}
        </span>
      ),
      width: '180px',
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => <RoleBadge roleKey={row.role} />,
      width: '110px',
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => (
        <span className="group-mappings-tab__muted">
          {formatDate(row.createdAt)}
          {row.createdBy ? ` by ${row.createdBy}` : ''}
        </span>
      ),
      width: '220px',
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          type="button"
          className="group-mappings-tab__delete"
          onClick={(e) => { e.stopPropagation(); openDelete(row); }}
          aria-label={`Delete mapping for ${row.role}`}
        >
          <Trash2 size={16} />
        </button>
      ),
      width: '70px',
      align: 'right',
    },
  ];

  const data = mappings || [];
  const totalCount = data.length;

  return (
    <div className="group-mappings-tab">
      <p className="group-mappings-tab__intro">
        M365 security groups in the client tenant are mapped to CareHub roles for this Client.
        When a federated user signs in, their group memberships are evaluated against these
        mappings to determine their role. Multiple mappings per Client are supported.
      </p>

      <div className="group-mappings-tab__toolbar">
        <button
          type="button"
          className="button-primary"
          onClick={openCreate}
        >
          <Plus size={16} /> Add group mapping
        </button>
      </div>

      <DataTable
        data={data}
        columns={columns}
        rowKey={(row) => row.groupMappingId}
        totalCount={totalCount}
        page={1}
        pageSize={PAGE_SIZE}
        onPageChange={() => {}}
        loading={loading}
        error={loadError}
        onRetry={fetchMappings}
        emptyMessage="No group mappings configured for this Client yet. Add a mapping above to begin federation user role resolution."
      />

      <ConfirmDialog
        isOpen={Boolean(deleteCandidate)}
        onClose={closeDelete}
        onConfirm={handleConfirmDelete}
        title="Remove group mapping"
        message={
          <>
            <p>
              Remove group mapping for role <strong>{deleteCandidate ? deleteCandidate.role : ''}</strong>?
            </p>
            <p>
              Users in this group will no longer be granted{' '}
              <strong>{deleteCandidate ? deleteCandidate.role : ''}</strong> access through new
              federated sign-ins.
            </p>
            <p>
              Existing user accounts that this mapping previously created will remain in place.
              They&apos;ll only be re-evaluated when those users next sign in via federation.
            </p>
            {deleteError && (
              <p className="group-mappings-tab__form-error" role="alert" style={{ marginTop: '0.75rem' }}>
                {deleteError}
              </p>
            )}
          </>
        }
        confirmLabel="Remove mapping"
        cancelLabel="Keep mapping"
        tone="danger"
        isSubmitting={inFlight === 'deleting'}
      />

      {/* 2b-13 Gate 5b — CarePeople module-role mappings (separate Gate-1 table). */}
      <CarePeopleRoleMappingsSection clientId={clientId} />

      {/* Role-model expansion Push 1 (2026-07-13) — CareCharge module-role mappings. */}
      <CareChargeRoleMappingsSection clientId={clientId} />

      {/* Role-model expansion Push 2 (2026-07-13) — CarePlan Care Coordinator mappings. */}
      <CarePlanRoleMappingsSection clientId={clientId} />

      {/* CareRota role model (2026-07-14) — CareRota module-role mappings. */}
      <CareRotaRoleMappingsSection clientId={clientId} />
    </div>
  );
}

// ── Create form sub-render ──────────────────────────────────

function renderCreateForm({ form, formErrors, inFlight, onChange, onSubmit, onCancel, picker }) {
  const showPicker = !picker.useGuidFallback;
  const errorMeta  = picker.errorCode ? PICKER_FALLBACK_COPY[picker.errorCode] || PICKER_FALLBACK_COPY.unknown : null;

  return (
    <form className="group-mappings-tab__form" onSubmit={onSubmit} noValidate>
      <h2 className="group-mappings-tab__form-title">Add group mapping</h2>

      {formErrors._top && (
        <div className="group-mappings-tab__form-error" role="alert">
          <p>{formErrors._top}</p>
          {formErrors._topSub && <p className="group-mappings-tab__form-error-sub">{formErrors._topSub}</p>}
        </div>
      )}

      {/* Picker fallback contextual error banner — shown above whichever
          input mode (picker or GUID) the form is currently in when a
          picker fetch failed. */}
      {errorMeta && (
        <div className="group-mappings-tab__picker-error" role="status">
          <div className="group-mappings-tab__picker-error-icon" aria-hidden="true">
            <AlertCircle size={18} />
          </div>
          <div className="group-mappings-tab__picker-error-body">
            <p className="group-mappings-tab__picker-error-title">{errorMeta.title}</p>
            <p className="group-mappings-tab__picker-error-text">{errorMeta.body}</p>
          </div>
          {errorMeta.canRetry && (
            <button
              type="button"
              className="group-mappings-tab__picker-error-retry"
              onClick={picker.onRetry}
              disabled={picker.state === 'loading'}
            >
              <RefreshCw size={14} /> Retry picker
            </button>
          )}
        </div>
      )}

      {showPicker && (
        <Field
          label="Group"
          help={
            picker.state === 'empty'
              ? 'No groups returned from this tenant. Make sure the federation app has been granted GroupMember.Read.All on the client tenant.'
              : 'Search by group display name or paste a GUID. Already-mapped groups are still selectable — uniqueness is per (group, role) pair.'
          }
          error={formErrors.groupObjectId}
          required
        >
          <div className="group-mappings-tab__picker" ref={picker.wrapperRef}>
            <div className="group-mappings-tab__picker-input">
              <Search size={14} className="group-mappings-tab__picker-input-icon" aria-hidden="true" />
              <input
                type="text"
                role="combobox"
                aria-label="Group"
                aria-expanded={picker.open}
                aria-controls="group-mappings-tab-listbox"
                aria-autocomplete="list"
                value={picker.input}
                onChange={(e) => {
                  picker.setInput(e.target.value);
                  picker.setOpen(true);
                  // Clear an existing selection if the user types over it.
                  if (picker.selectedId && e.target.value !== picker.input) {
                    picker.onClear();
                  }
                }}
                onFocus={() => picker.setOpen(true)}
                placeholder={picker.state === 'loading' ? 'Loading groups…' : 'Type to search…'}
                disabled={picker.state === 'loading'}
                autoComplete="off"
              />
              {picker.selectedId && (
                <button
                  type="button"
                  className="group-mappings-tab__picker-clear"
                  onClick={picker.onClear}
                  aria-label="Clear group selection"
                >
                  ×
                </button>
              )}
            </div>

            {picker.open && picker.state !== 'loading' && (
              <ul
                id="group-mappings-tab-listbox"
                role="listbox"
                className="group-mappings-tab__picker-list"
              >
                {picker.groups.length === 0 && (
                  <li className="group-mappings-tab__picker-empty" role="option" aria-selected="false">
                    {picker.state === 'empty'
                      ? 'No groups returned from this tenant.'
                      : 'No matches.'}
                  </li>
                )}
                {picker.groups.map((g) => {
                  const isSelected = g.id === picker.selectedId;
                  return (
                    <li
                      key={g.id}
                      role="option"
                      aria-selected={isSelected}
                      className={`group-mappings-tab__picker-option${isSelected ? ' is-selected' : ''}${g.alreadyMapped ? ' is-mapped' : ''}`}
                      onClick={() => picker.onSelect(g)}
                    >
                      <div className="group-mappings-tab__picker-option-main">
                        <span className="group-mappings-tab__picker-option-name">
                          {g.displayName || <em>(no display name)</em>}
                        </span>
                        <span className="group-mappings-tab__picker-option-id">{truncateGuid(g.id)}</span>
                      </div>
                      {g.alreadyMapped && (
                        <span className="group-mappings-tab__picker-option-badge">
                          Mapped ({(g.mappedRoles || []).join(', ')})
                        </span>
                      )}
                    </li>
                  );
                })}
                {picker.hasMore && (
                  <li className="group-mappings-tab__picker-truncated" role="option" aria-selected="false">
                    Showing first {picker.totalGroups} groups. Refine your search to narrow further.
                  </li>
                )}
              </ul>
            )}
          </div>
        </Field>
      )}

      {!showPicker && (
        <>
          <Field
            label="M365 security group GUID"
            help="GUID of the security group in the client tenant. Find this in the client tenant's Microsoft Entra admin centre → Groups → select group → Object ID."
            error={formErrors.groupObjectId}
            required
          >
            <input
              type="text"
              value={form.groupObjectId}
              onChange={(e) => onChange('groupObjectId', e.target.value)}
              placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
              autoComplete="off"
            />
          </Field>

          <Field
            label="Display name (optional)"
            help="Cached label for the admin UI. Has no impact on federation logic — runtime resolves via GUID. Leave blank if unsure."
          >
            <input
              type="text"
              value={form.groupDisplayName}
              onChange={(e) => onChange('groupDisplayName', e.target.value)}
              placeholder="e.g. Care Home Managers"
              autoComplete="off"
            />
          </Field>
        </>
      )}

      {/* Toggle between picker and GUID input. The picker state itself
          dictates whether the toggle text is "Enter GUID manually" (picker
          visible, GUID hidden) or "Use the group picker instead" (GUID
          visible, picker hidden). We hide the toggle entirely when the
          picker errored with a non-retryable code (federation_not_configured /
          federation_not_active) — the operator has no path back to a picker
          in that state until Tenant-level state changes. */}
      {(picker.state === 'loaded' || picker.state === 'empty' || (errorMeta && errorMeta.canRetry)) && (
        <div className="group-mappings-tab__picker-toggle">
          {showPicker ? (
            <button
              type="button"
              className="group-mappings-tab__link-button"
              onClick={() => picker.setUseGuidFallback(true)}
            >
              Enter group GUID manually
            </button>
          ) : (
            <button
              type="button"
              className="group-mappings-tab__link-button"
              onClick={() => {
                picker.setUseGuidFallback(false);
                if (picker.state === 'error') picker.onRetry();
              }}
            >
              Use the group picker instead
            </button>
          )}
        </div>
      )}

      <Field
        label="Role granted by this mapping"
        help="Auditor role users are invitation-only and cannot be granted via federation. Use the invitations flow for auditor access."
        error={formErrors.role}
        required
      >
        <select
          value={form.role}
          onChange={(e) => onChange('role', e.target.value)}
        >
          <option value="" disabled>Select a role…</option>
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Field>

      <div className="group-mappings-tab__form-actions">
        <button
          type="submit"
          className="button-primary"
          disabled={inFlight === 'creating'}
        >
          {inFlight === 'creating' ? 'Creating…' : 'Create mapping'}
        </button>
        <button type="button" className="button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
